import { Link } from 'react-router-dom'
import { motion, useReducedMotion } from 'framer-motion'
import { Section } from '@/components/ui/Section'
import { Container } from '@/components/ui/Container'
import { DecoratedHeader } from '@/components/ui/DecoratedHeader'
import { useBookingUrl } from '@/hooks/useBookingUrl'
import { smoothEase } from '@/lib/motion'
import { cn } from '@/lib/utils'

const servicePreviewCards = [
  {
    id: 'manicure',
    name: 'Manicure',
    startingAt: '$28',
    blurb: 'Shape, care, and polish or gel finish.',
  },
  {
    id: 'pedicure',
    name: 'Pedicure',
    startingAt: '$35',
    blurb: 'Soak, exfoliation, massage, and color.',
  },
  {
    id: 'gel-x',
    name: 'Gel X',
    startingAt: '$63',
    blurb: 'Structured extensions with a natural profile.',
  },
] as const

/** Soft organic blobs — top-right + bottom-left, like stationery corner accents. */
function ServiceCardCornerArt() {
  return (
    <div
      aria-hidden
      className="pointer-events-none absolute inset-0 overflow-hidden rounded-[inherit]"
    >
      <div
        className="absolute -right-10 -top-28 h-46 w-60 rounded-[58%_42%_52%_48%] bg-primary/8.5"
        style={{ transform: 'rotate(14deg)' }}
      />
      <div className="absolute -right-1 top-2 h-26 w-34 rounded-[48%_52%_50%_50%] bg-surface-strong/50" />
      <div
        className="absolute -bottom-32 -left-14 h-56 w-68 rounded-[52%_48%_48%_52%] bg-primary/6.5"
        style={{ transform: 'rotate(-10deg)' }}
      />
      <div className="absolute bottom-1 -left-6 h-24 w-32 rounded-[50%] bg-canvas-muted/75" />
    </div>
  )
}

export function HomeServicePreview() {
  const reduced = useReducedMotion()
  const bookingHref = useBookingUrl()

  return (
    <Section id="services" className="scroll-mt-24">
      <Container>
        <DecoratedHeader
          size="section"
          eyebrow="Signature"
          eyebrowAccent="Menu"
          title="Services"
          description="A few services guests ask for most often. Check the latest price when you book."
          sparkle={false}
        />
        <ul className="mt-10 grid list-none gap-5 p-0 sm:grid-cols-2 lg:grid-cols-3 lg:gap-6">
          {servicePreviewCards.map((card, i) => (
            <motion.li
              key={card.id}
              initial={reduced ? false : { opacity: 0, y: 16 }}
              whileInView={reduced ? undefined : { opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-40px' }}
              transition={{
                duration: reduced ? 0 : 0.48,
                delay: reduced ? 0 : Math.min(i * 0.06, 0.28),
                ease: smoothEase,
              }}
            >
              <Link
                to={bookingHref}
                className={cn(
                  'group relative flex h-full min-h-[260px] flex-col overflow-hidden rounded-2xl border border-hairline/80 bg-[#faf8f5]',
                  'text-center text-inherit no-underline shadow-[0_1px_0_rgba(255,255,255,0.7)_inset,0_8px_28px_-18px_rgba(42,38,36,0.08)]',
                  'transition-[border-color,box-shadow] duration-300',
                  'hover:border-primary/18 hover:shadow-[0_1px_0_rgba(255,255,255,0.85)_inset,0_22px_44px_-22px_rgba(120,77,54,0.14)]',
                  'focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-primary'
                )}
              >
                <ServiceCardCornerArt />
                <div className="relative z-10 flex flex-1 flex-col items-center justify-center px-8 py-10 sm:px-9">
                  <h3
                    className={cn(
                      'max-w-[18ch] font-display text-lg font-bold uppercase leading-snug tracking-[0.26em] sm:text-xl sm:tracking-[0.22em]',
                    )}
                  >
                    {card.name}
                  </h3>
                  <div
                    className="mx-auto mt-5 h-px w-14 bg-[color-mix(in_srgb,var(--color-primary)_22%,var(--color-hairline)_78%)]"
                    aria-hidden
                  />
                  <p className="mt-5 max-w-[26ch] text-sm leading-relaxed text-body/88">{card.blurb}</p>
                  <p className="mt-6 text-[0.65rem] font-semibold uppercase tracking-[0.22em] text-muted">
                    Starting at{' '}
                    <span className="text-base font-semibold tabular-nums tracking-normal text-body">
                      {card.startingAt}
                    </span>
                  </p>
                  <span
                    className={cn(
                      'mt-8 inline-flex min-h-10 shrink-0 items-center justify-center rounded-pill',
                      'border-2 border-primary bg-primary px-7 text-xs font-bold uppercase tracking-[0.2em] text-on-primary',
                      'shadow-[0_1px_0_rgba(255,255,255,0.2)_inset,0_4px_12px_-4px_rgba(62,40,28,0.35)]',
                      'transition-[background-color,border-color,box-shadow,transform] duration-200',
                      'group-hover:border-primary-active group-hover:bg-primary-active',
                      'group-hover:shadow-[0_1px_0_rgba(255,255,255,0.15)_inset,0_6px_16px_-4px_rgba(62,40,28,0.4)]',
                      'group-active:scale-[0.98]'
                    )}
                  >
                    Book now
                  </span>
                </div>
              </Link>
            </motion.li>
          ))}
        </ul>
      </Container>
    </Section>
  )
}
